import { useState, type ReactNode } from 'react';
import { fetchAlertEvents, fetchAlertRules, fetchAssets } from '../api/client';
import type { AlertEventResponse, AlertRuleResponse, AssetResponse } from '../api/types';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { EmptyState, ErrorCard, LoadingState } from '../states';
import { formatMetrik } from '../lib/telemetry-health';
import { umurSingkat } from '../lib/last-seen';
import {
  type BarisAlert,
  type FilterAlert,
  aturanDari,
  bangunFeed,
  namaAlatDari,
  ringkasFeed,
  saringFeed,
} from '../lib/alert-feed';

interface FeedSumber {
  readonly events: readonly AlertEventResponse[];
  readonly assets: readonly AssetResponse[];
  readonly rules: readonly AlertRuleResponse[];
}

const METRIK_LABEL: Record<string, string> = {
  cpu_percent: 'CPU',
  memory_percent: 'Memori',
  disk_percent: 'Disk',
  temperature_c: 'Suhu',
  uptime_seconds: 'Uptime',
};

const OPERATOR_KATA: Record<BarisAlert['operator'] & string, string> = {
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  eq: '=',
  ne: '≠',
};

const FILTERS: readonly { readonly value: FilterAlert; readonly label: string }[] = [
  { value: 'semua', label: 'Semua' },
  { value: 'terbuka', label: 'Terbuka' },
  { value: 'ambang', label: 'Ambang' },
  { value: 'offline', label: 'Offline' },
];

const SEVERITY_TONE: Record<string, { bg: string; fg: string; label: string }> = {
  critical: { bg: 'var(--danger-bg)', fg: 'var(--danger)', label: 'Kritis' },
  warning: { bg: 'var(--warn-bg)', fg: 'var(--warn)', label: 'Perhatian' },
  info: { bg: 'var(--neutral-bg)', fg: 'var(--muted)', label: 'Info' },
};
const OFFLINE_TONE = { bg: 'var(--line)', fg: 'var(--ink)', label: 'Offline' };

function Badge({ bg, fg, label }: { bg: string; fg: string; label: string }): ReactNode {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0.2rem 0.6rem',
        borderRadius: 'var(--r-pill)',
        fontSize: '0.75rem',
        fontWeight: 700,
        background: bg,
        color: fg,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}

/** Kalimat pemicu: metrik + nilai + ambang bila aturannya diketahui; jika tidak, kind + nilai. */
function labelPemicu(row: BarisAlert): string {
  if (row.kind === 'offline') return 'Tak ada kabar dari agen';
  if (row.metricKey === null) {
    return row.value === null ? 'Ambang terlampaui' : `Ambang terlampaui · nilai ${row.value}`;
  }
  const nama = METRIK_LABEL[row.metricKey] ?? row.metricKey;
  const nilai = formatMetrik(row.metricKey, row.value, null);
  const ambang =
    row.operator !== null && row.threshold !== null
      ? ` (ambang ${OPERATOR_KATA[row.operator]} ${formatMetrik(row.metricKey, row.threshold, null)})`
      : '';
  return `${nama} ${nilai}${ambang}`;
}

/**
 * Halaman Alert (ADR-0055 Fase 3) — kejadian ambang & offline SELURUH armada dalam satu layar.
 *
 * Dibaca dengan `workforce.telemetry.read`; nama alat dari `workforce.asset.read`. Severity, metrik,
 * dan ambang berasal dari aturan (`workforce.asset.update`) — bila izin itu tak ada, baris tetap
 * tampil tanpa pengayaan severity (degradasi aman, lihat lib/alert-feed). Kejadian offline tak
 * punya aturan dan selalu tampil sebagai "Offline".
 */
export function AlertsPage(): ReactNode {
  const { auth, me } = useSession();
  const canManageAgent = me.permissions.includes('workforce.asset.update');
  const [filter, setFilter] = useState<FilterAlert>('terbuka');

  const { state, reload } = useAsync<FeedSumber>(async () => {
    const [events, assets, rules] = await Promise.all([
      fetchAlertEvents(auth, {}),
      fetchAssets(auth),
      canManageAgent ? fetchAlertRules(auth) : Promise.resolve([]),
    ]);
    return { events, assets, rules };
  }, [auth, canManageAgent]);

  if (state.status === 'loading') return <LoadingState label="Memuat alert…" />;
  if (state.status === 'error') return <ErrorCard error={state.error} onRetry={reload} />;

  const rows = bangunFeed(
    state.data.events,
    namaAlatDari(state.data.assets),
    aturanDari(state.data.rules),
  );
  const ringkas = ringkasFeed(rows, new Date());
  const tampil = saringFeed(rows, filter);
  const now = new Date();

  return (
    <>
      <section className="card">
        <h2 style={{ marginBottom: '0.75rem' }}>Alert</h2>
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          {[
            { n: ringkas.terbuka, l: 'Terbuka', crit: ringkas.terbuka > 0 },
            { n: ringkas.offline, l: 'Offline', crit: false },
            { n: ringkas.pulih24j, l: 'Pulih 24 jam', crit: false },
          ].map((t) => (
            <div
              key={t.l}
              className="sunken"
              style={{
                flex: 1,
                background: 'var(--sunken)',
                borderRadius: 'var(--r-field)',
                padding: '0.6rem 0.7rem',
              }}
            >
              <strong
                style={{ fontSize: '1.5rem', color: t.crit ? 'var(--danger)' : 'var(--ink)' }}
              >
                {t.n}
              </strong>
              <div className="muted small">{t.l}</div>
            </div>
          ))}
        </div>
        <div className="row" style={{ marginTop: '0.75rem' }}>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              className="chip"
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
              style={
                filter === f.value
                  ? { background: 'var(--ink)', color: 'var(--ink-invert)' }
                  : undefined
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {tampil.length === 0 ? (
        <EmptyState
          title="Tidak ada alert"
          hint={
            filter === 'terbuka'
              ? 'Tidak ada alert terbuka — semua alat yang terpantau dalam ambang normal.'
              : 'Tidak ada kejadian yang cocok dengan saringan ini.'
          }
        />
      ) : (
        <section className="card">
          <ul className="list">
            {tampil.map((r) => {
              const tone =
                r.kind === 'offline'
                  ? OFFLINE_TONE
                  : (SEVERITY_TONE[r.severity ?? 'info'] ?? SEVERITY_TONE.info!);
              const terbuka = r.resolvedAt === null;
              return (
                <li key={r.id} className="list-item">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '0.5rem',
                      alignItems: 'flex-start',
                    }}
                  >
                    <strong>{r.namaAlat}</strong>
                    <Badge bg={tone.bg} fg={tone.fg} label={tone.label} />
                  </div>
                  <div className="muted small">{labelPemicu(r)}</div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '0.5rem',
                      alignItems: 'center',
                    }}
                  >
                    <span className="muted small">
                      {r.kind === 'offline' ? 'Offline' : 'Ambang'} · {umurSingkat(r.firedAt, now)}
                    </span>
                    <span
                      className="small"
                      style={{
                        fontWeight: 700,
                        padding: '0.15rem 0.55rem',
                        borderRadius: 'var(--r-pill)',
                        background: terbuka ? 'var(--danger-bg)' : 'var(--ok-bg)',
                        color: terbuka ? 'var(--danger)' : 'var(--ok)',
                      }}
                    >
                      {terbuka ? 'Terbuka' : 'Pulih'}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </>
  );
}
